import React, { useState, useEffect } from 'react';
import InstagramCarousel from './InstagramCarousel';

const CarouselTestPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use mock data directly for Instagram carousel testing
    const mockPosts = [
      {
        id: 'mock_post_001',
        title: 'But soft, what light through yonder window breaks?',
        content: 'It is the east, and Juliet is the sun!',
        slides: [
          {
            id: 'mock_slide_1',
            type: 'concept',
            title: 'The Balcony Scene',
            content: 'Romeo sees Juliet at her window and compares her to the sun. This famous soliloquy showcases Shakespeare\'s mastery of romantic poetry.',
            tags: ['metaphor', 'imagery', 'romance']
          },
          {
            id: 'mock_slide_2',
            type: 'analysis',
            title: 'Literary Analysis',
            content: 'Shakespeare uses celestial imagery to elevate Juliet above earthly beauty. The sun metaphor represents life, warmth, and divine beauty.',
            tags: ['symbolism', 'poetry', 'themes']
          },
          {
            id: 'mock_slide_3',
            type: 'quiz',
            title: 'Comprehension Check',
            content: 'Test your understanding of this famous passage.',
            question: 'What does Romeo compare Juliet to?',
            options: ['The moon', 'The sun', 'A star', 'A flower'],
            correct_answer: 1,
            explanation: 'Romeo calls Juliet the sun, symbolizing her brightness and life-giving power.'
          },
          {
            id: 'mock_slide_4',
            type: 'concept',
            title: 'Historical Context',
            content: 'Written in the 1590s, Romeo and Juliet reflects Elizabethan ideals of courtly love and fate.',
            tags: ['history', 'context', 'shakespeare']
          }
        ],
        entity: {
          id: 'romeo001',
          name: 'Romeo Montague',
          handle: '@romeo_montague',
          avatar_url: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          verified: true
        },
        liked_by_user: false,
        saved_by_user: false,
        likes: 2567,
        comments: 137,
        created_at: new Date().toISOString()
      },
      {
        id: 'mock_post_002',
        title: 'O Romeo, Romeo, wherefore art thou Romeo?',
        content: 'Deny thy father and refuse thy name!',
        slides: [
          {
            id: 'mock_slide_5',
            type: 'concept',
            title: 'Juliet\'s Dilemma',
            content: 'Juliet questions the significance of names and family identity in love.',
            tags: ['identity', 'love', 'conflict']
          },
          {
            id: 'mock_slide_6',
            type: 'analysis',
            title: 'Name vs Identity',
            content: 'What\'s in a name? This explores the tension between personal identity and family loyalty.',
            tags: ['philosophy', 'identity', 'family']
          },
          {
            id: 'mock_slide_7',
            type: 'interactive',
            title: 'Character Map',
            content: 'Explore Juliet\'s character development through this pivotal scene.',
            tags: ['character', 'development', 'interactive']
          }
        ],
        entity: {
          id: 'juliet001',
          name: 'Juliet Capulet',
          handle: '@juliet_capulet',
          avatar_url: 'https://images.unsplash.com/photo-1494790108755-2616c04ea525?w=150&h=150&fit=crop&crop=face',
          verified: true
        },
        liked_by_user: true,
        saved_by_user: false,
        likes: 3241,
        comments: 89,
        created_at: new Date().toISOString()
      }
    ];

    // Simulate loading delay
    setTimeout(() => {
      setPosts(mockPosts);
      setLoading(false);
    }, 1000);
  }, []);

  const handleLike = (postId, liked) => {
    console.log(`${liked ? 'Liked' : 'Unliked'} post ${postId}`);
    // Update local state
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, liked_by_user: liked, likes: post.likes + (liked ? 1 : -1) }
        : post
    ));
  };

  const handleSave = (postId, saved) => {
    console.log(`${saved ? 'Saved' : 'Unsaved'} post ${postId}`);
    // Update local state
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, saved_by_user: saved }
        : post
    ));
  };

  const handleComment = (postId) => {
    console.log(`Opening resources for post ${postId}`);
    alert('📚 Educational Resources:\n• Character analysis\n• Scene breakdown\n• Study questions\n• Related themes');
  };

  const handleProfileClick = (entity) => {
    console.log(`Opening profile for ${entity.name}`);
    alert(`Opening ${entity.name}'s profile page\n\nThis would show:\n• All their posts\n• Character background\n• Related scenes\n• Study materials`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Instagram-style feed...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Instagram-style Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between p-4">
            <div className="w-8"></div>
            <h1 className="text-xl font-bold text-gray-900">ScrollEd</h1>
            <div className="w-8 flex justify-end">
              <div className="text-gray-400 text-lg">💬</div>
            </div>
          </div>
        </div>
      </div>

      {/* Instagram-style Feed */}
      <div className="max-w-md mx-auto bg-white min-h-screen">
        <div className="pb-20">
          <div className="p-4 bg-blue-50 border-b border-blue-200">
            <h2 className="text-lg font-semibold text-blue-800">📱 Instagram Carousel Test</h2>
            <p className="text-sm text-blue-600">Testing slide-based educational content with swipe navigation</p>
          </div>
          
          {posts.map((post) => (
            <div key={post.id} className="p-4">
              <InstagramCarousel
                post={post}
                entity={post.entity}
                onLike={handleLike}
                onSave={handleSave}
                onComment={handleComment}
                onProfileClick={handleProfileClick}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Instagram-style Bottom Navigation */}
      <div className="fixed bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-md h-16 bg-white border-t border-gray-200 z-50">
        <div className="flex items-center justify-around h-full px-4">
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 bg-gray-800 rounded"></div>
            <span className="text-xs text-gray-800">Feed</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 bg-gray-400 rounded"></div>
            <span className="text-xs text-gray-400">Search</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 bg-gray-400 rounded"></div>
            <span className="text-xs text-gray-400">Reels</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 bg-gray-400 rounded"></div>
            <span className="text-xs text-gray-400">Saved</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-5 h-5 bg-gray-400 rounded"></div>
            <span className="text-xs text-gray-400">Profile</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarouselTestPage;